// Global packages and components
import { clsx } from 'clsx'
import { Link, useRouteLoaderData } from '@remix-run/react'
import { CheckIcon } from '@heroicons/react/24/solid'
import { ProductImage, Price, Button, Trustpilot } from '~/components'
import { getProductPageLink, hasVariations } from '~/utils'

// Types
import type { ProductSearchItem, RootDataProps } from '~/types'

interface Props extends ProductSearchItem {
  /**
   * Layout only. This must never gate which elements or which text render.
   * Grid and list have to produce identical markup once class attributes are
   * stripped, otherwise we are back to serving crawlers different content to
   * users, which is the cloaking risk this component was merged to remove.
   */
  layout?: 'grid' | 'list'
  /** Sets fetchpriority=high. Pass for the first row only, to help LCP. */
  priority?: boolean
}

// Main export
const ProductCard = ({
  name,
  shortDescription,
  productType,
  image,
  guidePrice,
  salePrice,
  url,
  options,
  bulletPoints,
  promotionName,
  variantStockCode,
  layout = 'grid',
  priority = false,
}: Props) => {
  const { productOverrides } = useRouteLoaderData('root') as RootDataProps

  const isList = layout === 'list'

  const link = getProductPageLink({
    productType: productType,
    stockCode: url.stockCode,
    slug: url.slug,
    variant: url?.option?.value,
  })

  // Find the override object for this stock code
  const override = productOverrides?.find(o =>
    o.productCodes?.includes(url.stockCode)
  )

  const buttonText = override?.buttonText ?? null
  const imageUrl = image?.imageUrl ?? 'https://placehold.co/600x400'

  // Parent listings have no variant code, so fall back to the parent's for reviews
  const sku = variantStockCode ?? url?.stockCode

  return (
    <article
      className={clsx('rounded-2xl bg-neutral-100 p-3 md:p-4', {
        'flex h-full flex-col': !isList,
        'grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-10': isList,
      })}
    >
      <Link
        to={link}
        className={clsx('block', isList ? 'lg:mb-4' : 'mb-4')}
      >
        <ProductImage
          src={imageUrl}
          alt={name || 'Product image'}
          hasVariations={options ? hasVariations(options) : false}
          tags={promotionName ? [{ label: promotionName, style: 'red' }] : []}
          priority={priority}
          showTagsMobile
        />
      </Link>
      <div className="flex flex-1 flex-col">
        <h3 className="mb-2 text-base font-semibold md:text-lg">
          <Link to={link}>{name}</Link>
        </h3>
        {sku && (
          <div
            key={sku}
            className="mb-2 h-[24px]"
          >
            <Trustpilot
              templateId="product-mini"
              product={{ sku, name }}
            />
          </div>
        )}
        {shortDescription && (
          <p
            className={clsx(
              'mb-2 break-words text-sm',
              isList ? 'line-clamp-4' : 'hidden lg:line-clamp-2'
            )}
          >
            {shortDescription}
          </p>
        )}
      </div>
      <div
        className={clsx({
          'mt-auto flex flex-col': !isList,
          'col-span-2 flex items-center gap-3 lg:col-span-1 lg:flex-col lg:items-stretch lg:gap-0':
            isList,
        })}
      >
        <div
          className={clsx({
            'flex-1 lg:mb-5 lg:flex-none': isList,
          })}
        >
          {productType == 'bulk' && <p className="mb-1 text-xs">From:</p>}
          <Price
            highlight
            price={guidePrice}
            salePrice={salePrice}
          />
        </div>
        {bulletPoints && bulletPoints.length > 0 && (
          <ul
            className={clsx(
              'mb-2',
              isList ? 'hidden lg:block' : 'hidden'
            )}
          >
            {bulletPoints.map(point => (
              <li
                key={point}
                className="mb-2 flex"
              >
                <CheckIcon className="mr-2 size-5 min-w-5 text-vibrant-green" />
                <span className="text-sm">{point}</span>
              </li>
            ))}
          </ul>
        )}
        <div
          className={clsx(
            isList ? 'flex-1 lg:mt-auto lg:flex-none lg:pt-5' : 'mt-auto pt-5'
          )}
        >
          <Button
            disabled={!link}
            to={link}
            title={buttonText ?? (productType == 'bulk' ? 'Hire now' : 'Buy now')}
            srOnlyText={name}
            style="tealGreen"
            fullWidth
          />
        </div>
      </div>
    </article>
  )
}

export default ProductCard
